import {CategoryPrediction} from '@mira/api';
import {i18n} from '@mira/i18n';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {ROW_TYPE_ICONS, ROW_TYPE_THRESHOLDS} from '../constants/categoryMapping';
import {TransformedCategory} from '../schemas';
import {getCategory} from './getCategory';
import {getRowType} from './getRowType';
import {getRulLabel} from './getRulLabel';
import {getSubcategory} from './getSubcategory';

const RUL_ONLY_WILL_NEED_THRESHOLD_DAYS = 365;

const RUL_ONLY_SYNTHETIC_CONFIDENCE = 0.3;

export function transformCategory(
  categoryId: string,
  prediction: CategoryPrediction | null | undefined,
  rulPrediction?: {rul_days: number | null} | null
): TransformedCategory | null {
  const CATEGORY = getCategory(categoryId);

  if (isNil(CATEGORY)) {
    return null;
  }

  if (isNil(prediction) && isNil(rulPrediction)) {
    return null;
  }

  const SUBCATEGORY = getSubcategory(CATEGORY.subcategoryId);

  const rulDays = isNotNil(rulPrediction) ? rulPrediction.rul_days : null;

  return match(prediction)
    .with(Pattern.nonNullable, (classifiedPrediction) => {
      const ROW_TYPE = getRowType(classifiedPrediction.will_need, classifiedPrediction.confidence);

      return {
        categoryKey: categoryId,
        will_need: classifiedPrediction.will_need,
        confidence: classifiedPrediction.confidence,
        rul_days: rulDays,

        displayName: i18n.t(`category.${CATEGORY.id}.name`),
        subcategoryId: CATEGORY.subcategoryId,
        mainGroupId: SUBCATEGORY.mainGroupId,
        rowType: ROW_TYPE,
        rowIcon: ROW_TYPE_ICONS[ROW_TYPE],

        percentage: Math.round(classifiedPrediction.confidence * 100),
        label: getRulLabel(
          classifiedPrediction.will_need,
          rulDays,
          classifiedPrediction.confidence
        ),
        operationDescription: i18n.t(`category.${CATEGORY.id}.operation`),
      };
    })
    .otherwise(() => {
      const synthesizedWillNeed = isNotNil(rulDays) && rulDays < RUL_ONLY_WILL_NEED_THRESHOLD_DAYS;
      const ROW_TYPE = getRowType(synthesizedWillNeed, RUL_ONLY_SYNTHETIC_CONFIDENCE);
      const labelConfidence = synthesizedWillNeed
        ? RUL_ONLY_SYNTHETIC_CONFIDENCE
        : ROW_TYPE_THRESHOLDS.DAMAGE;

      return {
        categoryKey: categoryId,
        will_need: synthesizedWillNeed,
        confidence: RUL_ONLY_SYNTHETIC_CONFIDENCE,
        rul_days: rulDays,

        displayName: i18n.t(`category.${CATEGORY.id}.name`),
        subcategoryId: CATEGORY.subcategoryId,
        mainGroupId: SUBCATEGORY.mainGroupId,
        rowType: ROW_TYPE,
        rowIcon: ROW_TYPE_ICONS[ROW_TYPE],

        percentage: 0,
        label: getRulLabel(synthesizedWillNeed, rulDays, labelConfidence),
        operationDescription: i18n.t(`category.${CATEGORY.id}.operation`),
      };
    });
}
